// Typed client for the LLC ledger (migration 278). Table reads go through
// PostgREST under RLS; every write is an RPC so the database decides.
import { supabase } from '../supabase';
import { call } from '../backbarApi';
import type {
  Account, AccessLevel, AccessRow, AssetInput, AuditRow, BankAccount, BankImportResult, BankImportRow,
  BankReconciliation, BankTransaction, DepreciationRun, EntrySource, FixedAsset, JournalEntry, JournalPage,
  LedgerLine, LineInput, Member, MemberCapital, MyAccess, Overview, Period, TrialBalanceRow, Vendor,
} from './types';

function unwrap<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
}

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await supabase.rpc(fn, args);
  return unwrap<T>(r as { data: T | null; error: { message: string } | null });
}

// ── access ───────────────────────────────────────────────────────────────────
export const access = {
  mine: () => rpc<MyAccess>('books_my_access'),
  list: () => rpc<AccessRow[]>('books_list_access'),
  set: (user_id: string, level: AccessLevel | null) => rpc<unknown>('books_set_access', { p_user_id: user_id, p_level: level }),
  setByEmail: (email: string, level: AccessLevel) => rpc<unknown>('books_set_access_by_email', { p_email: email, p_level: level }),
  /** Super admins only: emails a sign-in link and grants the level. */
  invite: (email: string, level: AccessLevel) =>
    call<{ user_id: string; email: string; level: AccessLevel; invited: boolean }>('books.invite', { email, level, origin: window.location.origin }),
  audit: async (limit = 200): Promise<AuditRow[]> =>
    unwrap(await supabase.from('backbar_audit_log').select('*')
      .or('target_type.like.books%,target_type.like.llc%').order('at', { ascending: false }).limit(limit)),
};

// ── chart of accounts ────────────────────────────────────────────────────────
export const accounts = {
  list: async (): Promise<Account[]> =>
    unwrap(await supabase.from('llc_accounts').select('*').order('display_order').order('code')),
  add: (p: { code: string; name: string; type: Account['type']; subtype: string; normal_balance?: Account['normal_balance'] | null; member?: Member | null; tax_line?: Account['tax_line'] | null }) =>
    rpc<Account>('llc_add_account', {
      p_code: p.code, p_name: p.name, p_type: p.type, p_subtype: p.subtype,
      p_normal_balance: p.normal_balance ?? null, p_member: p.member ?? null, p_tax_line: p.tax_line ?? null,
    }),
  update: (id: string, p: { name: string; subtype: string; tax_line: Account['tax_line'] | null; is_active: boolean; display_order: number }) =>
    rpc<Account>('llc_update_account', { p_id: id, p_name: p.name, p_subtype: p.subtype, p_tax_line: p.tax_line, p_is_active: p.is_active, p_display_order: p.display_order }),
  remove: (id: string) => rpc<void>('llc_delete_account', { p_id: id }),
};

// ── settings ─────────────────────────────────────────────────────────────────
export const settings = {
  all: async (): Promise<Record<string, unknown>> => {
    const rows = unwrap<{ key: string; value: unknown }[]>(await supabase.from('llc_settings').select('key, value'));
    return Object.fromEntries(rows.map((r) => [r.key, r.value]));
  },
  set: async (key: string, value: unknown): Promise<void> => {
    unwrap(await supabase.from('llc_settings').upsert({ key, value }));
  },
  /** books_settings.shares — the one source for member percentages. */
  shares: async (): Promise<Record<Member, number>> => {
    const r = unwrap<{ value: Record<Member, number> } | null>(await supabase.from('books_settings').select('value').eq('key', 'shares').maybeSingle());
    return r?.value ?? { leah: 51, laura: 49 };
  },
};

// ── journal ──────────────────────────────────────────────────────────────────
export const journal = {
  page: (p: { from: string; to: string; source?: EntrySource | null; search?: string | null; limit?: number; offset?: number }) =>
    rpc<JournalPage>('llc_journal', {
      p_from: p.from, p_to: p.to, p_source: p.source ?? null, p_search: p.search ?? null,
      p_limit: p.limit ?? 200, p_offset: p.offset ?? 0,
    }),
  post: (p: { entry_date: string; memo: string; lines: LineInput[]; source?: EntrySource; source_ref?: string | null; details?: Record<string, unknown> }) =>
    rpc<JournalEntry>('llc_post_entry', {
      p_entry_date: p.entry_date, p_memo: p.memo, p_lines: p.lines,
      p_source: p.source ?? 'manual', p_source_ref: p.source_ref ?? null, p_details: p.details ?? {},
    }),
  update: (id: string, p: { entry_date: string; memo: string; lines: LineInput[] }) =>
    rpc<JournalEntry>('llc_update_entry', { p_entry_id: id, p_entry_date: p.entry_date, p_memo: p.memo, p_lines: p.lines }),
  remove: (id: string) => rpc<void>('llc_delete_entry', { p_entry_id: id }),
  reverse: (id: string, date: string, reason: string) =>
    rpc<JournalEntry>('llc_reverse_entry', { p_entry_id: id, p_date: date, p_reason: reason }),
  openingBalances: (date: string, lines: LineInput[]) =>
    rpc<JournalEntry>('llc_record_opening_balances', { p_date: date, p_lines: lines }),
  recordIncome: (p: { date: string; amount_cents: number; income_account_id: string; deposit_account_id: string; memo: string; source_label: string }) =>
    rpc<JournalEntry>('llc_record_income', {
      p_date: p.date, p_amount_cents: p.amount_cents, p_income_account_id: p.income_account_id,
      p_deposit_account_id: p.deposit_account_id, p_memo: p.memo, p_source_label: p.source_label,
    }),
  recordExpense: (p: { date: string; amount_cents: number; expense_account_id: string; paid_from_account_id: string; vendor_id?: string | null; memo: string; receipt_url?: string | null }) =>
    rpc<JournalEntry>('llc_record_expense', {
      p_date: p.date, p_amount_cents: p.amount_cents, p_expense_account_id: p.expense_account_id,
      p_paid_from_account_id: p.paid_from_account_id, p_vendor_id: p.vendor_id ?? null, p_memo: p.memo, p_receipt_url: p.receipt_url ?? null,
    }),
};

// ── periods ──────────────────────────────────────────────────────────────────
export const periods = {
  list: async (): Promise<Period[]> => unwrap(await supabase.from('llc_periods').select('*').order('month', { ascending: false })),
  close: (month: string) => rpc<Record<string, unknown>>('llc_close_period', { p_month: month }),
  reopen: (month: string, reason: string) => rpc<Record<string, unknown>>('llc_reopen_period', { p_month: month, p_reason: reason }),
};

// ── vendors ──────────────────────────────────────────────────────────────────
export const vendors = {
  list: async (): Promise<Vendor[]> => unwrap(await supabase.from('llc_vendors').select('*').order('name')),
  upsert: async (v: Partial<Vendor> & { slug: string; name: string }): Promise<Vendor> =>
    unwrap(await supabase.from('llc_vendors').upsert(v, { onConflict: 'slug' }).select('*').single()),
  remove: async (id: string): Promise<void> => { unwrap(await supabase.from('llc_vendors').delete().eq('id', id)); },
};

// ── bank ─────────────────────────────────────────────────────────────────────
export const bank = {
  accounts: async (): Promise<BankAccount[]> => unwrap(await supabase.from('llc_bank_accounts').select('*').order('created_at')),
  addAccount: async (a: Omit<BankAccount, 'id' | 'created_at' | 'is_active'>): Promise<BankAccount> =>
    unwrap(await supabase.from('llc_bank_accounts').insert(a).select('*').single()),
  setActive: async (id: string, is_active: boolean): Promise<void> => { unwrap(await supabase.from('llc_bank_accounts').update({ is_active }).eq('id', id)); },
  transactions: async (bank_account_id: string, status?: BankTransaction['status']): Promise<BankTransaction[]> => {
    let q = supabase.from('llc_bank_transactions').select('*').eq('bank_account_id', bank_account_id).order('posted_date', { ascending: false }).limit(500);
    if (status) q = q.eq('status', status);
    return unwrap(await q);
  },
  forReviewCount: async (): Promise<number> => {
    const r = await supabase.from('llc_bank_transactions').select('id', { count: 'exact', head: true }).eq('status', 'for_review');
    if (r.error) throw new Error(r.error.message);
    return r.count ?? 0;
  },
  import: (bank_account_id: string, filename: string, rows: BankImportRow[]) =>
    rpc<BankImportResult>('llc_bank_import', { p_bank_account_id: bank_account_id, p_filename: filename, p_rows: rows }),
  categorize: (txn_id: string, account_id: string, vendor_id?: string | null, memo?: string | null) =>
    rpc<BankTransaction>('llc_bank_categorize', { p_txn_id: txn_id, p_account_id: account_id, p_vendor_id: vendor_id ?? null, p_memo: memo ?? null }),
  match: (txn_id: string, entry_id: string) => rpc<BankTransaction>('llc_bank_match', { p_txn_id: txn_id, p_entry_id: entry_id }),
  exclude: (txn_id: string, reason: string) => rpc<BankTransaction>('llc_bank_exclude', { p_txn_id: txn_id, p_reason: reason }),
  unpost: (txn_id: string) => rpc<BankTransaction>('llc_bank_unpost', { p_txn_id: txn_id }),
  reconcile: (bank_account_id: string, statement_date: string, statement_balance_cents: number, note: string) =>
    rpc<BankReconciliation>('llc_bank_reconcile', { p_bank_account_id: bank_account_id, p_statement_date: statement_date, p_statement_balance_cents: statement_balance_cents, p_note: note }),
  reconciliations: async (bank_account_id: string): Promise<BankReconciliation[]> =>
    unwrap(await supabase.from('llc_bank_reconciliations').select('*').eq('bank_account_id', bank_account_id).order('statement_date', { ascending: false })),
};

// ── assets ───────────────────────────────────────────────────────────────────
export const assets = {
  list: async (): Promise<FixedAsset[]> => unwrap(await supabase.from('llc_fixed_assets').select('*').order('in_service_on')),
  add: (p: AssetInput) => rpc<FixedAsset>('llc_add_asset', { p }),
  runs: async (asset_id?: string): Promise<DepreciationRun[]> => {
    let q = supabase.from('llc_depreciation_runs').select('*').order('month');
    if (asset_id) q = q.eq('asset_id', asset_id);
    return unwrap(await q);
  },
  runDepreciation: (month: string) => rpc<{ posted: number; total_cents: number }>('llc_run_depreciation', { p_month: month }),
  dispose: (asset_id: string, date: string, proceeds_cents: number, to_account_id: string, note: string) =>
    rpc<FixedAsset>('llc_dispose_asset', { p_asset_id: asset_id, p_date: date, p_proceeds_cents: proceeds_cents, p_to_account_id: to_account_id, p_note: note }),
};

// ── members ──────────────────────────────────────────────────────────────────
export const members = {
  capital: (year: number) => rpc<MemberCapital>('llc_member_capital', { p_year: year }),
  contribute: (p: {
    member: Member; date: string; kind: 'cash' | 'property' | 'expense_paid'; description: string; amount_cents: number;
    tax_basis_cents?: number | null; deposit_account_id?: string | null; expense_account_id?: string | null;
    asset?: Partial<AssetInput> | null; consent_ref?: string | null;
  }) => rpc<{ entry: JournalEntry; asset: FixedAsset | null }>('llc_record_contribution', {
    p_member: p.member, p_date: p.date, p_kind: p.kind, p_description: p.description, p_amount_cents: p.amount_cents,
    p_tax_basis_cents: p.tax_basis_cents ?? null, p_deposit_account_id: p.deposit_account_id ?? null,
    p_expense_account_id: p.expense_account_id ?? null, p_asset: p.asset ?? null, p_consent_ref: p.consent_ref ?? null,
  }),
  distribute: (p: { member: Member; date: string; amount_cents: number; from_account_id: string; method: string; reference: string }) =>
    rpc<JournalEntry>('llc_record_distribution', {
      p_member: p.member, p_date: p.date, p_amount_cents: p.amount_cents, p_from_account_id: p.from_account_id, p_method: p.method, p_reference: p.reference,
    }),
};

// ── reports ──────────────────────────────────────────────────────────────────
export const reports = {
  trialBalance: (from: string, to: string) => rpc<TrialBalanceRow[]>('llc_trial_balance', { p_from: from, p_to: to }),
  ledger: (from: string, to: string, account_id?: string | null) =>
    rpc<LedgerLine[]>('llc_general_ledger', { p_from: from, p_to: to, p_account_id: account_id ?? null }),
  overview: () => rpc<Overview>('llc_overview'),
  postAppleMonth: (month: string) => rpc<Record<string, unknown>>('llc_post_apple_month', { p_month: month }),
};

export const FAR_PAST = '1900-01-01';
